function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { Type as FilterType } from '@deephaven/filters';
import Log from '@deephaven/log';
import { StorageUtils } from '@deephaven/storage';
import { PromiseUtils } from '@deephaven/utils';
import { siftPrunableItems } from "./pouchCommandHistoryUtils.js";
import PouchStorageTable from "./PouchStorageTable.js";
var COMMAND_HISTORY_ITEMS_MAX = 2500;
var COMMAND_HISTORY_ITEMS_PRUNE = 2000;
var log = Log.module('PouchCommandHistoryTable');
export class PouchCommandHistoryTable extends PouchStorageTable {
  constructor(language, cache) {
    var _this$cache$tableRegi;
    super("CommandHistoryStorage.".concat(language), {
      // Optimizations to cut down on growing table size. These should be safe
      // since we don't care about revision history for command history
      // documents.
      auto_compaction: true,
      revs_limit: 1
    });

    // Add this table instance to the table registry
    _defineProperty(this, "searchText", void 0);
    this.cache = cache;
    if (!this.cache.tableRegistry.has(this.cacheKey)) {
      this.cache.tableRegistry.set(this.cacheKey, new Set());
    }
    log.debug('Adding table to registry', this.cacheKey);
    (_this$cache$tableRegi = this.cache.tableRegistry.get(this.cacheKey)) === null || _this$cache$tableRegi === void 0 || _this$cache$tableRegi.add(this);
  }
  get cacheKey() {
    return this.db.name;
  }
  setSearch(searchText) {
    log.debug('Setting search filters', searchText);
    this.searchText = searchText.trim().toLowerCase();
    this.setFilters(searchText ? [StorageUtils.makeFilterConfig([StorageUtils.makeFilterItem('name', FilterType.contains, searchText)])] : []);
  }

  // Our current version of eslint + prettier doesn't always like the `override`
  // keyword. Whenever we upgrade, we should annotate this function with `override`.
  dbUpdate(event) {
    log.debug('Clearing cache and refreshing data', event);
    this.cache.response.delete(this.cacheKey);
    super.dbUpdate(event);
  }

  /**
   * Fetch command history data from `this.cache.response` or from
   * PouchDB if data is not found in the cache. If the number of total items in
   * the db exceeds COMMAND_HISTORY_ITEMS_MAX, the database will be pruned down
   * to COMMAND_HISTORY_ITEMS_PRUNE total items. Note that PouchDB doesn't
   * actually remove them from the underlying IndexDB, they are just marked
   * as deleted and won't be present in our query results.
   * @param selector
   */
  fetchData(_selector) {
    var _this = this;
    return _asyncToGenerator(function* () {
      // Wrapping this in a setTimeout so that it executes on next call stack.
      // This is necessary to ensure `this.cache` has been initialized due to some
      // nuances with property initilization with inherited classes.
      return PromiseUtils.withTimeout(0, /*#__PURE__*/_asyncToGenerator(function* () {
        if (_this.cache.response.has(_this.cacheKey)) {
          log.debug('Fetching from cache', _this.searchText, _this.viewport);
        } else {
          log.debug('Fetching from PouchDB', _this.searchText, _this.viewport);
          _this.cache.response.set(_this.cacheKey, _this.db.allDocs({
            include_docs: true
          }).then(result => {
            var allItems = result.rows
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            .map(row => row.doc).filter(_ref2 => {
              var name = _ref2.name;
              return name;
            });
            log.debug("Fetched ".concat(allItems.length, " command history items"));
            var _siftPrunableItems = siftPrunableItems(allItems, COMMAND_HISTORY_ITEMS_MAX, COMMAND_HISTORY_ITEMS_PRUNE),
              toKeep = _siftPrunableItems.toKeep,
              toPrune = _siftPrunableItems.toPrune;

            // If number of items in PouchDB has exceeded COMMAND_HISTORY_ITEMS_MAX
            // prune them down so we have COMMAND_HISTORY_ITEMS_PRUNE left
            if (toPrune.length) {
              _this.pruneItems(toPrune);
            }
            return {
              docs: toKeep
            };
          }));
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        var result = _this.cache.response.get(_this.cacheKey);
        if (_this.searchText == null || _this.searchText === '') {
          return result;
        }
        return _objectSpread(_objectSpread({}, result), {}, {
          docs: (yield result).docs.filter(_ref3 => {
            var name = _ref3.name;
            return (
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              name.toLowerCase().includes(_this.searchText)
            );
          })
        });
      }));
    })();
  }

  /**
   * Override `PouchStorageTable.fetchInfo` so we can make use of
   * `PouchCommandHistoryCache`
   * @param selector
   */
  // Our current version of eslint + prettier doesn't always like the `override`
  // keyword. Whenever we upgrade, we should annotate this function with `override`.
  fetchInfo(selector) {
    var _this2 = this;
    return _asyncToGenerator(function* () {
      return _this2.fetchData(selector);
    })();
  }

  /**
   * Override `PouchStorageTable.fetchViewportData` to fetch command history
   * storage items. This override provides caching + optimizations specific to
   * command history. We cache the results to avoid excessive PouchDB queries
   * during scrolling. The cache should only need to be invalidated when search
   * filter changes or commands are added to the storage history.
   *
   * @param viewport
   * @param selector
   * @param sort
   * @returns Promise to array of command history storage items
   */
  // Our current version of eslint + prettier doesn't always like the `override`
  // keyword. Whenever we upgrade, we should annotate this function with `override`.
  fetchViewportData(viewport, selector, sort) {
    var _this3 = this;
    return _asyncToGenerator(function* () {
      var data = yield _this3.fetchData(selector);
      log.debug('Fetching viewport data', viewport.top, viewport.bottom + 1, sort, data);
      var isReversed = typeof sort[0] === 'object' && sort[0].id === 'desc';
      var docs = isReversed ? data.docs.slice(0).reverse() : data.docs;
      var items = docs.slice(viewport.top, viewport.bottom + 1);
      return {
        items,
        offset: viewport.top
      };
    })();
  }

  /**
   * Mark given items as `_deleted` in the database.
   * @param items
   */
  pruneItems(items) {
    var _this4 = this;
    return _asyncToGenerator(function* () {
      if (_this4.cache.isPruning.get(_this4.cacheKey) === true) {
        log.debug('Pruning already in progress. Skipping.');
        return;
      }
      try {
        log.debug("Pruning ".concat(items.length, " command history items"));

        // Disable change notifications while we bulk delete to avoid locking up
        // the app
        var resumeListeners = _this4.cache.pauseChangeListeners(_this4.cacheKey);
        _this4.cache.isPruning.set(_this4.cacheKey, true);
        yield _this4.db.bulkDocs(items.map(item => _objectSpread(_objectSpread({}, item), {}, {
          _deleted: true
        })));
        _this4.cache.isPruning.set(_this4.cacheKey, false);
        resumeListeners();
        log.debug('Finished pruning command history items');
      } catch (err) {
        log.error('An error occurred while pruning db', err);
      }
    })();
  }

  // Our current version of eslint + prettier doesn't always like the `override`
  // keyword. Whenever we upgrade, we should annotate this function with `override`.
  close() {
    var _this$cache$tableRegi2, _this$cache$tableRegi3, _this$changes;
    log.debug('Closing table', this, (_this$cache$tableRegi2 = this.cache.tableRegistry.get(this.cacheKey)) === null || _this$cache$tableRegi2 === void 0 ? void 0 : _this$cache$tableRegi2.size);
    (_this$cache$tableRegi3 = this.cache.tableRegistry.get(this.cacheKey)) === null || _this$cache$tableRegi3 === void 0 || _this$cache$tableRegi3.delete(this);
    (_this$changes = this.changes) === null || _this$changes === void 0 || _this$changes.cancel();
    super.close();
  }
}
export default PouchCommandHistoryTable;
//# sourceMappingURL=PouchCommandHistoryTable.js.map